import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import CustomModal from "../../../../packages/CustomModal";
import WPHeading from "../../../../packages/WPHeading";

import useTimezone from "../../../../hooks/useTimezone";

import AssignLicense from "../../AssignLicense";
import RenewLicense from "../RenewLicense";
import TerminateLicense from "../TerminateLicense";
import { LicenseDetailsSection } from "./ViewUserDetails";

import {
  CustomModalButtons,
  DATE_FORMATS,
} from "../../../../constants/global.constants";
import { LICENSE_VIEW_USER_MODAL_KEYS } from "../../../../constants/LicenseManagement.constants";

import {
  CalendarIcon,
  HistoryIcon,
  IdCardIcon,
} from "../../../../assets/icons";

import "./LicenseViewUserDetails.scss";

const LicenseViewUserDetails = ({
  userData,
  onCloseModal,
  onUserLicenseUpdate,
}) => {
  const { t } = useTranslation();
  const { formatTime } = useTimezone();

  const [userDetails, setUserDetails] = useState(userData);
  const [licenseModalState, setLicenseModalState] = useState(null);

  const {
    name = "",
    email = "",
    startDate = "",
    lastLogin = "",
    companyName = "",
  } = userDetails || {};

  const { assignModal, renewModal, terminateModal } =
    LICENSE_VIEW_USER_MODAL_KEYS;

  const handleConfirmBtnClick = ({ target }) => {
    if (target?.id === CustomModalButtons.CONFIRM) {
      setLicenseModalState({ type: assignModal });
    } else {
      onCloseModal();
    }
  };

  const handleCloseModalAction = () => {
    setLicenseModalState(null);
  };

  const handleUpsertUserLicense = (userId, licenseObj) => {
    if (!licenseObj) {
      setLicenseModalState(null);
      return;
    }

    setUserDetails((details) => {
      const licenses = Array.isArray(details?.licenses) ? details.licenses : [];
      const licenseIndex = licenses.findIndex(
        (license) => license.id === licenseObj.id,
      );

      let updatedLicenses;
      if (licenseIndex === -1) {
        updatedLicenses = [...licenses, licenseObj];
      } else {
        updatedLicenses = [...licenses];
        updatedLicenses[licenseIndex] = {
          ...licenses[licenseIndex],
          ...licenseObj,
        };
      }

      return {
        ...details,
        licenses: updatedLicenses,
      };
    });

    onUserLicenseUpdate?.(userId || userDetails?.userId, licenseObj);
    setLicenseModalState(null);
  };

  const userDetailsInfo = [
    {
      id: "user-since-info",
      label: t("licenseManagement.userSince"),
      value: formatTime(startDate, true, DATE_FORMATS.MMM_DD_YYYY),
      icon: <CalendarIcon />,
    },
    {
      id: "last-login-info",
      label: t("licenseManagement.lastLoginColumn"),
      value: formatTime(lastLogin, true, DATE_FORMATS.MMM_DD_YYYY),
      icon: (
        <HistoryIcon style={{ color: "var(--du-color-primary-aquamarine)" }} />
      ),
    },
    {
      id: "company-details-info",
      label: t("licenseManagement.companyDetails"),
      value: companyName,
      icon: <IdCardIcon />,
    },
  ];

  const handleLicenseAction = (action, licenseId) => {
    setLicenseModalState({ type: action, licenseId });
  };

  return (
    <>
      <CustomModal
        customclass="license-view-user-modal"
        header={name}
        headerHelper={email}
        confirmBtn={t("licenseManagement.assignLicense")}
        onModalClose={handleConfirmBtnClick}
        width="685px"
        profileInitials={name?.charAt(0)?.toUpperCase()}
        invisible={licenseModalState !== null}
        headerDataTestId="view_user_license"
      >
        <div className="license-view-user-details">
          <div className="license-user-details">
            {userDetailsInfo.map((info) => (
              <div className="license-user-details-row" key={info.id}>
                <div className="license-user-icon">
                  <span className="license-user-icon-svg">{info.icon}</span>
                  <WPHeading
                    className="license-user-details-label"
                    size="small"
                    text={info.label}
                    id={`${info.id}-label`}
                    secondary
                    color="var(--du-color-grey)"
                    fontNoto
                  />
                </div>
                <WPHeading
                  size="small"
                  text={info.value || "-"}
                  id={`${info.id}-value`}
                  secondary
                  type="default"
                  fontNoto
                  tooltip={info.value || "-"}
                  className="license-user-company-name"
                />
              </div>
            ))}
          </div>
          <div className="license-details-section">
            <LicenseDetailsSection
              userData={userDetails}
              onLicenseAction={handleLicenseAction}
              onLicenseUpdate={handleUpsertUserLicense}
            />
          </div>
        </div>
      </CustomModal>

      {licenseModalState?.type === assignModal && (
        <AssignLicense
          userData={userDetails}
          onCloseModal={handleCloseModalAction}
          onUserLicenseUpdate={handleUpsertUserLicense}
        />
      )}

      {licenseModalState?.type === renewModal && (
        <RenewLicense
          userData={userDetails}
          selectedLicenseId={licenseModalState.licenseId}
          onCloseModal={handleCloseModalAction}
          onUserLicenseUpdate={handleUpsertUserLicense}
        />
      )}

      {licenseModalState?.type === terminateModal && (
        <TerminateLicense
          userData={userDetails}
          selectedLicenseId={licenseModalState.licenseId}
          onCloseModal={handleCloseModalAction}
          onUserLicenseUpdate={handleUpsertUserLicense}
        />
      )}
    </>
  );
};

export default LicenseViewUserDetails;
